import { apiClient } from '../apiClient'

export interface FeedbackTheme {
  theme: string
  description: string
  frequency: number
  sentiment: 'positive' | 'neutral' | 'negative'
  examples: string[]
}

export interface FeedbackInsight {
  type: 'strength' | 'development_area' | 'recommendation'
  title: string
  description: string
  confidence: number
  supporting_evidence: string[]
}

export interface FeedbackAnalysisResponse {
  summary: string
  key_themes: FeedbackTheme[]
  strengths: string[]
  development_areas: string[]
  insights: FeedbackInsight[]
  recommendations: string[]
  sentiment_analysis: {
    overall: 'positive' | 'neutral' | 'negative'
    positive_count: number
    neutral_count: number
    negative_count: number
  }
}

export interface FeedbackAnalysisRequest {
  feedback_request_id: string
  requested_for_name: string
  request_title: string
  responses: Array<{
    stakeholder_name: string
    stakeholder_role?: string
    category: string
    responses: Array<{
      question_text: string
      answer: string
    }>
    overall_rating?: number
    general_comments?: string
  }>
  is_confidential: boolean
}

// ==================== Team Analytics Types ====================

export interface TeamAnalyticsInsight {
  id: string
  type: 'risk' | 'strength' | 'opportunity' | 'action' | 'development'
  title: string
  description: string
  confidence: number
  impact: 'high' | 'medium' | 'low'
  category: string
  recommendedActions: string[]
  relatedMembers?: string[]
}

export interface TeamAnalyticsRequest {
  managerId: string
  teamSize: number
  avgProgress: number
  performanceDistribution: {
    exceptional: number
    full: number
    notYetFull: number
    weak: number
    unrated: number
  }
  objectiveMetrics: {
    total: number
    onTrack: number
    atRisk: number
    offTrack: number
    pending: number
  }
  reviewCycleStatus: Array<{
    type: string
    completed: number
    pending: number
    overdue: number
  }>
  kpaPerformance: Array<{
    kpaName: string
    teamAvg: number
    memberCount: number
  }>
  idpMetrics?: {
    coverage: number
    interventionCompletionRate: number
    totalDevelopmentAreas: number
  }
  atRiskMembers: Array<{
    name: string
    status: string
    progress: number
  }>
}

export interface TeamAnalyticsResponse {
  insights: TeamAnalyticsInsight[]
  generatedAt: string
  summary: string
}

export const aiService = {
  /**
   * Analyze feedback using AI
   */
  async analyzeFeedback(request: FeedbackAnalysisRequest): Promise<FeedbackAnalysisResponse> {
    try {
      const response = await apiClient.post<FeedbackAnalysisResponse>('/ai/api/feedback/analyze', request)
      return response.data
    } catch (error: any) {
      console.error('AI analysis failed:', error)
      throw error
    }
  },

  /**
   * Generate AI-powered team analytics insights
   * Uses the existing insights endpoint with team-level data aggregation
   */
  async getTeamAnalyticsInsights(request: TeamAnalyticsRequest, bustCache = false): Promise<TeamAnalyticsResponse> {
    try {
      // Build a comprehensive team-level objective for the insights endpoint
      // Append timestamp when busting cache to force new AI generation
      const cacheBreaker = bustCache ? `-${Date.now()}` : ''
      const teamObjective = {
        objectiveId: `team-analytics-${request.managerId}${cacheBreaker}`,
        title: `Team Performance Analytics (${request.teamSize} members)`,
        description: buildTeamDescription(request),
        progress: request.avgProgress,
        status: request.atRiskMembers.length > 0 ? 'at_risk' : 'on_track',
        kpis: request.kpaPerformance.map(kpa => ({
          name: kpa.kpaName,
          current_value: kpa.teamAvg,
          target_value: 100,
          unit: '%'
        })),
        strategicContext: {
          pillar: 'Team Performance Management',
          kpa: 'Manager Analytics'
        }
      }

      const response = await apiClient.post<{ insights: TeamAnalyticsInsight[]; generatedAt: string }>(
        '/ai/api/insights/objectives',
        teamObjective
      )

      // Normalize AI types to our UI categories and enrich
      const enrichedInsights = (response.data.insights || []).map((insight: any, idx: number) => ({
        ...insight,
        id: insight.id || `team-insight-${idx}`,
        type: normalizeInsightType(insight.type),
        relatedMembers: insight.type === 'risk' ? request.atRiskMembers.map(m => m.name) : undefined
      }))

      return {
        insights: enrichedInsights,
        generatedAt: response.data.generatedAt || new Date().toISOString(),
        summary: generateTeamSummary(request)
      }
    } catch (error: any) {
      console.error('Team analytics AI insights failed:', error)
      // Return a graceful fallback instead of throwing
      return {
        insights: generateFallbackInsights(request),
        generatedAt: new Date().toISOString(),
        summary: generateTeamSummary(request)
      }
    }
  }
}

// ==================== Helper Functions ====================

/**
 * Normalize AI-returned insight types to our supported UI categories.
 * The backend insights endpoint returns types like 'optimization', 'alignment',
 * 'cross-pillar synergies' etc. which don't map to our styled categories.
 */
function normalizeInsightType(rawType: string): TeamAnalyticsInsight['type'] {
  const normalized = (rawType || '').toLowerCase().trim()
  const typeMap: Record<string, TeamAnalyticsInsight['type']> = {
    'risk': 'risk',
    'strength': 'strength',
    'opportunity': 'opportunity',
    'action': 'action',
    'development': 'development',
    // Map AI-returned types to our categories
    'optimization': 'action',
    'alignment': 'strength',
    'strategic alignment': 'strength',
    'cross-pillar synergies': 'opportunity',
    'cross-pillar': 'opportunity',
    'recommendation': 'action',
    'performance': 'action',
    'trend': 'opportunity',
    'milestone': 'action',
    'resource': 'action',
    'collaboration': 'opportunity'
  }
  return typeMap[normalized] || 'opportunity'
}

function buildTeamDescription(request: TeamAnalyticsRequest): string {
  const pd = request.performanceDistribution
  const om = request.objectiveMetrics

  const parts = [
    // Platform context — prevents the AI from suggesting we "implement" things that already exist
    `CONTEXT: This analysis is for a manager using the Eduvos Performance Platform — a comprehensive performance management system for a South African higher education institution. The platform already provides: KPI tracking with targets and actuals, objective management with SMART analysis, performance review cycles (Q1/mid-year/Q3/year-end), evidence-based KPA ratings, Individual Development Plans (IDPs) with interventions, stakeholder feedback with AI analysis, calibration sessions, and strategic cascade alignment. DO NOT suggest implementing tracking tools, dashboards, or analytics — those already exist in this platform. Instead, focus on PEOPLE actions the manager should take based on the data below.`,
    '',
    `TEAM DATA:`,
    `- Team size: ${request.teamSize} direct reports`,
    `- Average objective progress: ${request.avgProgress}%`,
    `- Performance ratings: ${pd.exceptional} exceptional, ${pd.full} full, ${pd.notYetFull} not yet full, ${pd.weak} weak, ${pd.unrated} unrated`,
    `- Objectives: ${om.total} total — ${om.onTrack} on-track, ${om.atRisk} at-risk, ${om.offTrack} off-track, ${om.pending} pending approval`
  ]

  if (request.atRiskMembers.length > 0) {
    parts.push(`- At-risk/off-track members: ${request.atRiskMembers.map(m => `${m.name} (${m.progress}% progress, ${m.status})`).join('; ')}`)
  }

  if (request.reviewCycleStatus.length > 0) {
    const completed = request.reviewCycleStatus.reduce((s, r) => s + r.completed, 0)
    const overdue = request.reviewCycleStatus.reduce((s, r) => s + r.overdue, 0)
    const pending = request.reviewCycleStatus.reduce((s, r) => s + r.pending, 0)
    parts.push(`- Review cycle: ${completed} completed, ${pending} pending, ${overdue} overdue`)
    if (overdue > 0) {
      const overdueTypes = request.reviewCycleStatus.filter(r => r.overdue > 0).map(r => r.type).join(', ')
      parts.push(`  Overdue review types: ${overdueTypes}`)
    }
  }

  if (request.kpaPerformance.length > 0) {
    parts.push(`- KPA performance (team averages):`)
    request.kpaPerformance.forEach(kpa => {
      const status = kpa.teamAvg >= 70 ? 'strong' : kpa.teamAvg >= 40 ? 'needs attention' : 'critical'
      parts.push(`  ${kpa.kpaName}: ${kpa.teamAvg}% (${status}, ${kpa.memberCount} contributors)`)
    })
  }

  if (request.idpMetrics) {
    parts.push(`- IDP coverage: ${request.idpMetrics.coverage}% of team has active development plans`)
    parts.push(`- Intervention completion rate: ${request.idpMetrics.interventionCompletionRate}%`)
    parts.push(`- Total development areas identified: ${request.idpMetrics.totalDevelopmentAreas}`)
  }

  parts.push('')
  parts.push(`INSTRUCTIONS: Generate 3-5 insights focused on PEOPLE ACTIONS this manager should take. Each insight should reference specific data points above. Categories should be: risk (performance/compliance concerns), strength (what's working well), opportunity (growth potential), action (urgent tasks), development (learning/growth areas). Do NOT suggest implementing tools or dashboards — the platform handles that. Focus on coaching conversations, recognition, workload management, review scheduling, and team development.`)

  return parts.join('\n')
}

function generateTeamSummary(request: TeamAnalyticsRequest): string {
  const { performanceDistribution: pd, objectiveMetrics: om, avgProgress } = request

  if (om.offTrack > 0 || pd.weak > 0) {
    return `Your team requires attention — ${om.offTrack} objectives are off-track and ${pd.weak} team members rated weak. Focus on targeted support and recovery plans.`
  }

  if (om.atRisk > 2 || avgProgress < 50) {
    return `Team performance is mixed — ${om.atRisk} objectives are at risk with ${avgProgress}% average progress. Consider scheduling check-ins for members falling behind.`
  }

  if (pd.exceptional > 0 && avgProgress >= 70) {
    return `Strong team performance — ${pd.exceptional} exceptional performers and ${avgProgress}% average progress. Continue building momentum and recognising achievements.`
  }

  return `Team is progressing steadily with ${avgProgress}% average progress across ${om.total} objectives. Monitor at-risk areas and maintain regular check-ins.`
}

function generateFallbackInsights(request: TeamAnalyticsRequest): TeamAnalyticsInsight[] {
  const insights: TeamAnalyticsInsight[] = []

  // At-risk insight
  if (request.atRiskMembers.length > 0) {
    insights.push({
      id: 'fallback-risk-1',
      type: 'risk',
      title: 'Team Members Requiring Support',
      description: `${request.atRiskMembers.length} team member(s) are flagged as at-risk or off-track. Proactive engagement can prevent further decline.`,
      confidence: 0.9,
      impact: 'high',
      category: 'Performance Risk',
      recommendedActions: [
        'Schedule 1-on-1 check-ins with at-risk members',
        'Review their objectives for blockers or resource gaps',
        'Consider adjusting timelines or providing additional support'
      ],
      relatedMembers: request.atRiskMembers.map(m => m.name)
    })
  }

  // Review overdue insight
  const overdueReviews = request.reviewCycleStatus.reduce((sum, r) => sum + r.overdue, 0)
  if (overdueReviews > 0) {
    insights.push({
      id: 'fallback-review-1',
      type: 'action',
      title: 'Overdue 1:1 Check-In Dialogues',
      description: `${overdueReviews} review(s) are overdue. Timely reviews are critical for team member development and compliance.`,
      confidence: 0.95,
      impact: 'high',
      category: 'Review Compliance',
      recommendedActions: [
        'Prioritize scheduling overdue reviews this week',
        'Use the review preparation assistant for efficient meetings'
      ]
    })
  }

  // Positive performance insight
  if (request.performanceDistribution.exceptional > 0) {
    insights.push({
      id: 'fallback-strength-1',
      type: 'strength',
      title: 'Exceptional Performers Identified',
      description: `${request.performanceDistribution.exceptional} team member(s) are performing at an exceptional level. Leverage their expertise for team upliftment.`,
      confidence: 0.85,
      impact: 'medium',
      category: 'Talent Recognition',
      recommendedActions: [
        'Recognise exceptional performers in team settings',
        'Consider mentoring or coaching assignments',
        'Explore stretch projects for continued growth'
      ]
    })
  }

  // IDP coverage
  if (request.idpMetrics && request.idpMetrics.coverage < 80) {
    insights.push({
      id: 'fallback-dev-1',
      type: 'development',
      title: 'Development Plan Coverage Gap',
      description: `Only ${request.idpMetrics.coverage}% of your team has active Individual Development Plans. Full coverage supports career growth and retention.`,
      confidence: 0.8,
      impact: 'medium',
      category: 'Development',
      recommendedActions: [
        'Initiate IDP discussions during next check-ins',
        'Prioritize team members without active development plans'
      ]
    })
  }

  // Progress insight
  if (insights.length === 0) {
    insights.push({
      id: 'fallback-progress-1',
      type: 'opportunity',
      title: 'Team Progress Update',
      description: `Your team is at ${request.avgProgress}% average progress with ${request.objectiveMetrics.total} objectives. ${request.objectiveMetrics.onTrack} are on track.`,
      confidence: 0.9,
      impact: 'low',
      category: 'Progress Tracking',
      recommendedActions: [
        'Review KPA performance radar for areas needing attention',
        'Celebrate team wins to maintain momentum'
      ]
    })
  }

  return insights
}

export default aiService
